'use client';

import { useCallback, useState } from 'react';

import {
    type AccountPaymentsSuccessResponse,
    logSentryError,
    SentryAcpCreditCardsErrors as SentryErrors,
} from '@libs/common-next';

import { SnackbarUtils } from '../../utils';
import { useCvvChallenge } from './CVVChallengeProvider';
import { CVV_CHALLENGE_COPY } from './constants';

interface UseCvvFormParams {
    card: AccountPaymentsSuccessResponse;
    onVerified: () => Promise<void>;
}

/** Submits the CVV form. Once the last attempt fails, the provider reports the limit and the parent moves on. */
export function useCvvForm({ card, onVerified }: UseCvvFormParams) {
    const { validateCvv } = useCvvChallenge();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>();

    const completeVerification = useCallback(async () => {
        try {
            await onVerified();
        } catch {
            // onVerified logs the failure and rethrows. This step shows the one error and stays put.
            SnackbarUtils.showPredefined('SERVER_ERROR');
        }
    }, [onVerified]);

    const submitCvv = useCallback(
        async (encryptedSecurityCode: string) => {
            setIsSubmitting(true);
            setErrorMessage(undefined);

            try {
                const { isValid, remainingAttempts } = await validateCvv(card.cardId, encryptedSecurityCode);

                if (isValid) {
                    await completeVerification();
                    return;
                }

                if (remainingAttempts > 0) {
                    setErrorMessage(CVV_CHALLENGE_COPY.remainingAttempts(remainingAttempts));
                }
            } catch (error) {
                logSentryError(error, SentryErrors.CVVChallenge.ValidateCvvFailed);
                SnackbarUtils.showPredefined('SERVER_ERROR');
            } finally {
                setIsSubmitting(false);
            }
        },
        [card.cardId, completeVerification, validateCvv]
    );

    const clearError = useCallback(() => setErrorMessage(undefined), []);

    return {
        isSubmitting,
        errorMessage,
        submitCvv,
        clearError,
    };
}
